import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n';
import { useMinisiteMenu } from './useMinisiteMenu';
import dotsIcon from '../../assets/dots-horizontal.svg';
import eyeIcon from '../../assets/icons/eye-outline.svg';
import trashIcon from '../../assets/icons/trash-outline.svg';
import pencilIcon from '../../assets/icons/pencil-outline.svg';
import plusIcon from '../../assets/icons/plus-primary.svg';
import playIcon from '../../assets/icons/play-outline.svg';

const MinisiteCard = ({ minisite, onEdit, onPublish, onDelete, onAddPage, isOwner, onViewDetails }) => {
    const { t } = useI18n();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const closeMenu = useCallback(() => setDropdownOpen(false), []);
    const { triggerRef, menuRef, menuId } = useMinisiteMenu(Boolean(isOwner && dropdownOpen), closeMenu);
    const pageCount = minisite.active_pages_count ?? (minisite.pages?.filter(p => p.status)?.length || 0);

    useEffect(() => {
        if (!isOwner) setDropdownOpen(false);
    }, [isOwner]);

    return (
        <div className="ms-card">
            {/* Row 1: Name + dots menu */}
            <div className="ms-card-top">
                <span className="ms-card-name">{minisite.title}</span>
                {isOwner && <div className="ms-card-dots">
                    <button
                        type="button"
                        ref={triggerRef}
                        className="ms-card-dots-btn"
                        onClick={() => setDropdownOpen(v => !v)}
                        title="More options"
                        aria-expanded={dropdownOpen}
                        aria-controls={dropdownOpen ? menuId : undefined}
                    >
                        <img src={dotsIcon} alt="More" width={24} height={24} />
                    </button>

                    {dropdownOpen && createPortal(
                        <div className="ms-card-dropdown" id={menuId} ref={menuRef}
                            role="group" aria-label="Minisite actions" onClick={e => e.stopPropagation()}>
                            {isOwner && (
                                <>
                                    <button
                                        className="ms-card-dropdown-item"
                                        onClick={() => { setDropdownOpen(false); onAddPage && onAddPage(minisite); }}
                                    >
                                        <img src={plusIcon} alt="" width={18} height={18} />
                                        {t('pages.minisite.add_page')}
                                    </button>
                                    <button
                                        className="ms-card-dropdown-item"
                                        onClick={() => { setDropdownOpen(false); onEdit && onEdit(minisite); }}
                                    >
                                        <img src={pencilIcon} alt="" width={18} height={18} />
                                        Edit
                                    </button>
                                </>
                            )}
                            <div className="ms-dropdown-divider"></div>
                            {isOwner && (
                                <button
                                    className="ms-card-dropdown-item ms-card-dropdown-danger"
                                    onClick={() => { setDropdownOpen(false); onDelete && onDelete(minisite); }}
                                >
                                    <img src={trashIcon} alt="" width={18} height={18} />
                                    Delete
                                </button>
                            )}
                        </div>, document.body
                    )}
                </div>}
            </div>

            {/* Row 2: Action buttons */}
            <div className="ms-card-actions">
                <span className="ms-card-slug-chip">/{minisite.slug}</span>
                <span className="ms-card-pages">{pageCount} {pageCount === 1 ? 'Page' : 'Pages'}</span>
            </div>

            {/* Row 3: Pages count + slug chip */}
            <div className="ms-card-bottom">
                <button
                    className="ms-card-btn-addpage"
                    onClick={() => onAddPage ? onAddPage(minisite) : onEdit && onEdit(minisite)}
                >
                    Details
                </button>
                <button
                    className="ms-card-btn-publish"
                    onClick={() => {
                        window.open(`/public/minisite/${minisite.slug}`, '_blank', 'noopener,noreferrer');
                    }}
                >
                    <img src={playIcon} alt="" width={16} height={16} />
                    Preview
                </button>
            </div>
        </div>
    );
};

export default MinisiteCard;
